<template>
  <el-select
    v-if="useSelect"
    v-model="inner"
    size="small"
    class="quick-status-select"
    :disabled="loading || disabled"
    @change="submit"
  >
    <el-option
      v-for="item in options"
      :key="String(item.value)"
      :label="item.label"
      :value="item.value"
    />
  </el-select>
  <el-switch
    v-else
    v-model="inner"
    :loading="loading"
    :disabled="disabled"
    :active-value="activeValue"
    :inactive-value="inactiveValue"
    :active-text="activeLabel"
    :inactive-text="inactiveLabel"
    inline-prompt
    @change="submit"
  />
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from "vue";
import { ElMessage } from "element-plus";

const props = defineProps({
  /** 当前状态值 */
  modelValue: {
    type: [Number, String],
    required: true,
  },
  /** 行 id */
  id: {
    type: [Number, String],
    required: true,
  },
  /** 枚举 [{label, value}] */
  options: {
    type: Array,
    required: true,
  },
  /** 更新接口，如 Item/updateStatus */
  url: {
    type: String,
    required: true,
  },
  /** 提交字段名，默认 status */
  field: {
    type: String,
    default: "status",
  },
  /** select | switch | auto */
  mode: {
    type: String,
    default: "auto",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "success", "fail"]);
const { proxy } = getCurrentInstance();
const loading = ref(false);
const inner = ref(props.modelValue);

watch(
  () => props.modelValue,
  (v) => {
    inner.value = v;
  }
);

const useSelect = computed(() => {
  if (props.mode === "select") return true;
  if (props.mode === "switch") return false;
  return !(props.options && props.options.length === 2);
});

const activeValue = computed(() => props.options?.[0]?.value ?? 0);
const inactiveValue = computed(() => props.options?.[1]?.value ?? 1);
const activeLabel = computed(() => props.options?.[0]?.label ?? "");
const inactiveLabel = computed(() => props.options?.[1]?.label ?? "");

const submit = async (val) => {
  const prev = props.modelValue;
  loading.value = true;
  try {
    const payload = {
      id: props.id,
      field: props.field,
      [props.field]: val,
    };
    if (props.field === "status") {
      payload.status = val;
    }
    const res = await proxy.$http({
      method: "post",
      url: props.url,
      data: payload,
    });
    if (res.code == 0) {
      emit("update:modelValue", val);
      emit("success", val);
      ElMessage.success(res.msg || "已更新");
    } else {
      inner.value = prev;
      emit("fail", res);
      ElMessage.error(res.msg || "更新失败");
    }
  } catch (e) {
    inner.value = prev;
    emit("fail", e);
    ElMessage.error(e?.message || "更新失败");
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.quick-status-select {
  width: 110px;
}
</style>
